import {
  Box,
  Fade,
  Stack,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material'
import { type MouseEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { createSegmentedControlSx } from '../styles'
import {
  type DeliverySource,
  type DeliverySourceType,
  type HistorySection
} from '../types'
import DeliverySourceDetails from './DeliverySourceDetails'
import SendsTable from './SendsTable'
import SourcesTable from './SourcesTable'

type HistoryViewProps = {
  initialSection?: HistorySection
  initialSendsSourceType?: DeliverySourceType | null
}

const HistoryView = ({
  initialSection = 'sends',
  initialSendsSourceType = null
}: HistoryViewProps) => {
  const [selectedSection, setSelectedSection] = useState<HistorySection>(initialSection)
  const [selectedSource, setSelectedSource] = useState<DeliverySource | null>(null)
  const { t } = useTranslation()
  const handleSectionChange = (
    _event: MouseEvent<HTMLElement>,
    nextSection: HistorySection | null
  ) => {
    if (nextSection === null) {
      return
    }

    setSelectedSection(nextSection)
    setSelectedSource(null)
  }

  if (selectedSource) {
    return (
      <DeliverySourceDetails
        source={selectedSource}
        onBack={() => setSelectedSource(null)}
      />
    )
  }

  const sourceType: DeliverySourceType = selectedSection === 'trigger'
    ? 'trigger'
    : 'campaign'

  return (
    <Fade in timeout={180}>
      <Stack gap={2} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Box sx={{ alignSelf: 'flex-start', flexShrink: 0 }}>
          <ToggleButtonGroup
            value={selectedSection}
            exclusive
            onChange={handleSectionChange}
            size="small"
            aria-label="Contenu du tableau"
            sx={createSegmentedControlSx(96)}
          >
            <ToggleButton value="sends">{t('mailing.history.sending')}</ToggleButton>
            <ToggleButton value="campaign">{t('mailing.history.campaigns')}</ToggleButton>
            <ToggleButton value="trigger">{t('mailing.history.triggers')}</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box
          sx={{
            display: selectedSection === 'sends' ? 'flex' : 'none',
            flex: '1 1 auto',
            minHeight: 0
          }}
        >
          <SendsTable initialSourceType={initialSendsSourceType} />
        </Box>

        {selectedSection !== 'sends' && (
          <SourcesTable
            sourceType={sourceType}
            onSelect={setSelectedSource}
          />
        )}
      </Stack>
    </Fade>
  )
}

export default HistoryView
